import {
    ChangeDetectionStrategy,
    Component,
    effect,
    type ElementRef,
    input,
    output,
    viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgIcon } from '@ng-icons/core';
import {
    QpxTextareaAutosize,
    QpxButton,
    QpxFormFieldDirectives,
    QpxIcon,
} from '@quipex/shared/ui';
import type { QuippyAssistantAppearance } from '../../../runtime';

@Component({
    selector: 'qpx-quippy-message-edit-panel',
    imports: [
        QpxTextareaAutosize,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        NgIcon,
        QpxButton,
        QpxFormFieldDirectives,
        QpxIcon,
    ],
    templateUrl: './edit-panel.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'flex w-full max-w-2xl min-w-0 flex-col gap-3',
    },
})
export class QuippyMessageEditPanel {
    public readonly messageId = input.required<string>();
    public readonly appearance = input<QuippyAssistantAppearance>('elevated');
    public readonly draft = input.required<string>();
    public readonly canResend = input.required<boolean>();
    public readonly fieldLabel = input.required<string>();

    public readonly draftChanged = output<string>();
    public readonly editCancelled = output<void>();
    public readonly resendRequested = output<void>();

    private readonly editInput =
        viewChild<ElementRef<HTMLTextAreaElement>>('editInput');

    constructor() {
        effect(() => {
            const editInput = this.editInput()?.nativeElement;
            if (!editInput) return;

            queueMicrotask(() => {
                editInput.focus();
                editInput.select();
            });
        });
    }

    protected onInput(event: Event): void {
        this.draftChanged.emit((event.target as HTMLTextAreaElement).value);
    }

    protected onKeyDown(event: KeyboardEvent): void {
        if (event.isComposing) return;

        if (event.key === 'Escape') {
            event.preventDefault();
            this.editCancelled.emit();
            return;
        }

        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            this.resendRequested.emit();
        }
    }
}
